/**
 * ActualizarFirmaModal — re-captura la firma manuscrita del asociado
 * desde NeoCore. Pensado para que el operador use NeoCore en una tablet
 * (el asociado firma con el dedo) o en desktop (firma con el mouse).
 *
 * Implementa SignaturePad en HTML canvas vanilla — no usa el componente
 * de la app móvil (que es RN-only). El output respeta el mismo formato
 * SignatureData del onboarding:
 *   { paths: string[], width: number, height: number }
 *
 * Llama la RPC `actualizar_firma_asociado` que update la columna
 * `asociado.firma_paths` y deja audit log. La firma queda disponible
 * inmediatamente para futuros pagarés/documentos.
 */
import { useEffect, useRef, useState } from 'react'
import { CheckCircle, Eraser, X as XIcon } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  nombreCompleto: string
  noAsociado: string
  onSuccess?: () => void
}

const CANVAS_W = 560
const CANVAS_H = 220

export function ActualizarFirmaModal({
  open, onClose, asociadoId, nombreCompleto, noAsociado, onSuccess,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawingRef = useRef(false)
  const currentPathRef = useRef<string[]>([])      // points del path en curso ('Mx,y' / 'Lx,y')
  const pathsRef = useRef<string[]>([])             // todos los paths completados
  const [hasInk, setHasInk] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Reset al abrir
  useEffect(() => {
    if (!open) return
    pathsRef.current = []
    currentPathRef.current = []
    setHasInk(false)
    setError(null)
    setSuccess(false)
    // Limpiar el canvas tras el primer paint
    setTimeout(() => {
      const c = canvasRef.current
      if (c) {
        const ctx = c.getContext('2d')!
        ctx.clearRect(0, 0, c.width, c.height)
        // Línea-guía de firma muy sutil
        ctx.strokeStyle = '#e5e7eb'
        ctx.lineWidth = 1
        ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(20, CANVAS_H - 30)
        ctx.lineTo(CANVAS_W - 20, CANVAS_H - 30)
        ctx.stroke()
        ctx.setLineDash([])
      }
    }, 0)
  }, [open])

  // Coordenada touch/mouse → canvas
  const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!
    const rect = c.getBoundingClientRect()
    const scaleX = c.width / rect.width
    const scaleY = c.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    canvasRef.current?.setPointerCapture(e.pointerId)
    drawingRef.current = true
    const p = getPoint(e)
    currentPathRef.current = [`M${p.x.toFixed(1)},${p.y.toFixed(1)}`]
    const ctx = canvasRef.current!.getContext('2d')!
    ctx.strokeStyle = '#14171c'
    ctx.lineWidth = 2.6
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return
    e.preventDefault()
    const p = getPoint(e)
    currentPathRef.current.push(`L${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    const ctx = canvasRef.current!.getContext('2d')!
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
  }
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return
    e.preventDefault()
    drawingRef.current = false
    canvasRef.current?.releasePointerCapture(e.pointerId)
    if (currentPathRef.current.length > 1) {
      pathsRef.current.push(currentPathRef.current.join(' '))
      setHasInk(true)
    }
    currentPathRef.current = []
  }

  const onLimpiar = () => {
    pathsRef.current = []
    currentPathRef.current = []
    setHasInk(false)
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')!
    ctx.clearRect(0, 0, c.width, c.height)
    ctx.strokeStyle = '#e5e7eb'
    ctx.lineWidth = 1
    ctx.setLineDash([4, 4])
    ctx.beginPath()
    ctx.moveTo(20, CANVAS_H - 30)
    ctx.lineTo(CANVAS_W - 20, CANVAS_H - 30)
    ctx.stroke()
    ctx.setLineDash([])
  }

  const onGuardar = async () => {
    if (!hasInk) return
    setSubmitting(true)
    setError(null)
    const { error: rpcErr } = await supabase.rpc('actualizar_firma_asociado', {
      p_asociado_id: asociadoId,
      p_paths: pathsRef.current,
      p_canvas_w: CANVAS_W,
      p_canvas_h: CANVAS_H,
      p_origen: 'neocore_tablet',
    })
    setSubmitting(false)
    if (rpcErr) { setError(rpcErr.message); return }
    setSuccess(true)
    onSuccess?.()
  }

  if (!open) return null

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,25,0.55)', zIndex: 999 }}
      />
      <div
        role="dialog"
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 'min(640px, 92vw)', background: '#fff', borderRadius: 16,
          boxShadow: '0 30px 60px rgba(0,0,0,0.28)', zIndex: 1000,
          padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
              Actualizar firma manuscrita
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
              {nombreCompleto} · {noAsociado}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ marginLeft: 'auto', background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--muted-foreground)' }}
            aria-label="Cerrar"
          >
            <XIcon size={20} />
          </button>
        </div>

        {success ? (
          <div style={{
            padding: '24px', borderRadius: 12,
            background: 'rgba(15,138,87,0.08)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center',
          }}>
            <CheckCircle size={36} weight="duotone" color="var(--ok-ink, #0f8a57)" />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--ok-ink, #0f8a57)' }}>
              Firma actualizada
            </p>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
              Se va a usar automáticamente en los próximos pagarés y documentos del asociado.
            </p>
            <button className="btn btn-primary" onClick={onClose} style={{ marginTop: 8 }}>
              Cerrar
            </button>
          </div>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
              Pedile al asociado que firme dentro del recuadro con el dedo (si NeoCore está abierto
              en una tablet) o con el mouse. La firma queda guardada como su firma de identidad
              para futuros documentos.
            </p>

            <div style={{
              border: '2px solid var(--brand)', borderRadius: 12, overflow: 'hidden',
              background: '#fff', touchAction: 'none', position: 'relative',
            }}>
              <canvas
                ref={canvasRef}
                width={CANVAS_W}
                height={CANVAS_H}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                style={{
                  display: 'block', width: '100%', height: 'auto',
                  cursor: 'crosshair', touchAction: 'none',
                }}
              />
              {!hasInk && (
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none',
                  fontSize: 11, color: 'var(--n-300)',
                  letterSpacing: 0.15, textTransform: 'uppercase', fontWeight: 700,
                }}>
                  Firmar acá
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={onLimpiar}
                disabled={!hasInk}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  background: 'transparent', border: '1px solid var(--hairline)',
                  fontSize: 13, fontWeight: 600,
                  color: hasInk ? 'var(--ink)' : 'var(--muted-foreground)',
                  cursor: hasInk ? 'pointer' : 'not-allowed',
                  opacity: hasInk ? 1 : 0.55,
                }}
              >
                <Eraser size={13} weight="bold" /> Limpiar
              </button>
              <div style={{ flex: 1 }} />
              {error && (
                <span style={{ fontSize: 12, color: 'var(--danger, #a32d2d)' }}>{error}</span>
              )}
              <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button
                className="btn btn-primary"
                onClick={onGuardar}
                disabled={!hasInk || submitting}
              >
                {submitting ? 'Guardando…' : 'Guardar firma'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  )
}
